import { PaginatedResponse } from "@/types/api";
import type { Employee } from "@/types/employee";
import { apiUrl } from "../../utils/url";
import { HttpClient, HttpError } from "../HttpClient";
import { isAbortError, toServiceError } from "../errors";
import {
  EmployeeSearchOptions,
  EmployeeSearchParameters,
  GetEmployeesWithAvailableSlotsParams,
  IEmployeesService,
} from "./IEmployeesService";

export class EmployeesService implements IEmployeesService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 60000, // 1 min
      headers: {},
    });
  }

  async getEmployees(): Promise<PaginatedResponse<Employee>> {
    try {
      return await this.httpClient.get<PaginatedResponse<Employee>>(
        "/employees/all",
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
      );
    }
  }

  async searchEmployees(
    parameters: EmployeeSearchParameters = {},
    options: EmployeeSearchOptions = {},
  ): Promise<PaginatedResponse<Employee>> {
    const { page = 0, size = 20, sort, signal } = options;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    if (sort) queryParams.set("sort", sort);

    try {
      return await this.httpClient.post<PaginatedResponse<Employee>>(
        `/employees/search?${queryParams.toString()}`,
        parameters,
        signal ? { signal } : {},
      );
    } catch (error) {
      // Aborts and HTTP errors as-is: the caller drops the abort and
      // translates the backend's error code. Anything else is the network.
      if (isAbortError(error) || error instanceof HttpError) {
        throw error;
      }
      throw new Error(
        "Não foi possível carregar os colaboradores. Verifique a ligação e tente novamente.",
      );
    }
  }

  async getEmployeeById(id: string, signal?: AbortSignal): Promise<Employee> {
    try {
      return await this.httpClient.get<Employee>(
        `/employees/${id}`,
        signal ? { signal } : {},
      );
    } catch (error) {
      // Re-throw aborts and HTTP errors as-is: callers handle the abort, and
      // translate the backend's error code from the HttpError.
      if (isAbortError(error) || error instanceof HttpError) {
        throw error;
      }
      throw toServiceError(
        error,
        "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
      );
    }
  }

  async getEmployeeByExternalId(externalId: string): Promise<Employee> {
    try {
      return await this.httpClient.get<Employee>(
        `/employees/external-code/${externalId}`,
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
      );
    }
  }

  async getCurrentEmployee(): Promise<Employee> {
    try {
      return await this.httpClient.get<Employee>(
        "/employees/employee-from-token",
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
      );
    }
  }

  async getEmployeesByStoreUUID(storeUUID: string): Promise<Employee[]> {
    try {
      return await this.httpClient.get<Employee[]>(
        `/employees/store/${storeUUID}`,
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
      );
    }
  }

  async getEmployeesWithAvailableSlots(
    params: GetEmployeesWithAvailableSlotsParams,
  ): Promise<Employee[]> {
    try {
      return await this.httpClient.post<Employee[]>(
        "/employees/with-available-slots",
        params,
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar funcionários com disponibilidade. Altere os filtros ou tente novamente.",
      );
    }
  }
}
